import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { InboxPanel } from "@/components/inbox-panel";
import { GmailFolderView } from "@/components/gmail-folder-view";
import { EmailDraftsTab } from "@/components/email-drafts-tab";
import { ColdFolderView } from "@/components/cold-folder-view";
import { MessengerPanel } from "@/components/messenger/messenger-panel";
import { Inbox as InboxIcon, Send, FileText, AlertTriangle, Lock, Loader2, Mail, Snowflake, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/hooks/use-workspace";

type Folder = "inbox" | "drafts" | "sent" | "outbox";
type Lane = "warm" | "cold" | "messages";

// Task #244 — inbox/drafts/sent read Gmail mailbox data and therefore need the
// restricted read scope. Outbox is app-managed drafts and stays available.
const READ_DEPENDENT_FOLDERS: Folder[] = ["inbox", "drafts", "sent"];

type GmailReadStatus = {
  inboundReadingAvailable: boolean;
  readAccessState: "none" | "requested" | "rejected" | "granted_pending_connect" | "connected";
};

function InboxComingSoon({ state }: { state?: GmailReadStatus["readAccessState"] }) {
  const requested = state === "requested";
  const granted = state === "granted_pending_connect";
  const { buildPath } = useWorkspace();
  return (
    <div className="h-full flex items-center justify-center" data-testid="inbox-coming-soon">
      <div className="max-w-md text-center space-y-3 px-6">
        <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <Lock className="h-6 w-6 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-semibold">Unified Inbox — coming soon</h2>
        <p className="text-sm text-muted-foreground">
          Reading your mailbox inside Clarik uses a restricted Google permission that's rolling out
          gradually. {granted
            ? "Your access is approved — finish connecting it from Settings."
            : requested
              ? "Your access request is pending review."
              : "Request access to join the beta."}
          {" "}You can still compose and send email from the Outbox.
        </p>
        <Link href="/settings?tab=connections">
          <Button data-testid="button-inbox-request-access">
            {granted ? "Finish connecting" : requested ? "View request status" : "Request inbox access"}
          </Button>
        </Link>
      </div>
    </div>
  );
}

const FOLDERS: Array<{ key: Folder; label: string; icon: typeof InboxIcon }> = [
  { key: "inbox", label: "Inbox", icon: InboxIcon },
  { key: "drafts", label: "Drafts", icon: FileText },
  { key: "sent", label: "Sent", icon: Send },
  { key: "outbox", label: "Outbox", icon: AlertTriangle },
];

function readInitialFolder(): Folder {
  if (typeof window === "undefined") return "inbox";
  const q = new URLSearchParams(window.location.search).get("folder");
  return q === "drafts" || q === "sent" || q === "outbox" ? q : "inbox";
}

function readInitialDraftId(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("draftId");
}

function readInitialLane(): Lane {
  if (typeof window === "undefined") return "warm";
  const l = new URLSearchParams(window.location.search).get("lane");
  return l === "cold" || l === "messages" ? l : "warm";
}

export default function InboxPage() {
  const [lane, setLane] = useState<Lane>(readInitialLane);
  const [folder, setFolder] = useState<Folder>(readInitialFolder);
  const [outboxInitialDraftId, setOutboxInitialDraftId] = useState<string | null>(readInitialDraftId);

  const { data: gmailStatus, isLoading: gmailStatusLoading } = useQuery<GmailReadStatus>({
    queryKey: ["/api/gmail/status"],
  });
  const readAvailable = gmailStatus?.inboundReadingAvailable ?? false;

  // Outbox badge — count of the caller's pending (queued/failed) sends so
  // the folder label surfaces how many emails are waiting without opening it.
  const { data: outboxPulse } = useQuery<{ count: number; needsReconnect: boolean }>({
    queryKey: ["/api/email-drafts/pending-count"],
    refetchInterval: 60_000,
  });
  const outboxCount = outboxPulse?.count ?? 0;
  const outboxNeedsReconnect = !!outboxPulse?.needsReconnect;

  // Keep the URL in sync when the user changes folders so refreshes and
  // shared links land back where they were.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (folder === "inbox") {
      url.searchParams.delete("folder");
    } else {
      url.searchParams.set("folder", folder);
    }
    if (lane === "cold" || lane === "messages") {
      url.searchParams.set("lane", lane);
    } else {
      url.searchParams.delete("lane");
    }
    // draftId is only meaningful while we're on the outbox folder.
    if (folder !== "outbox" && url.searchParams.has("draftId")) {
      url.searchParams.delete("draftId");
      setOutboxInitialDraftId(null);
    }
    window.history.replaceState({}, "", url.toString());
  }, [folder, lane]);

  return (
    <div className="flex flex-col h-[calc(100dvh-3.5rem)] overflow-hidden" data-testid="page-inbox">
      <div className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <InboxIcon className="w-6 h-6" />
            <div>
              <h1 className="text-2xl font-semibold">Email Hub</h1>
              <p className="text-sm text-muted-foreground">Your inbox, drafts, sent, and outbox — all in one place.</p>
            </div>
          </div>

          {/* Warm (Gmail) vs Cold (Maildoso/SMTP relay) lane. The cold lane has
              its own transport and is intentionally NOT gated by Gmail read scope. */}
          <div className="inline-flex items-center rounded-md border p-0.5 shrink-0" data-testid="email-hub-lane-toggle">
            <button
              onClick={() => setLane("warm")}
              data-testid="lane-warm"
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors",
                lane === "warm" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Mail className="w-4 h-4 shrink-0" />
              <span>Warm</span>
            </button>
            <button
              onClick={() => setLane("cold")}
              data-testid="lane-cold"
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors",
                lane === "cold" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Snowflake className="w-4 h-4 shrink-0" />
              <span>Cold</span>
            </button>
            <button
              onClick={() => setLane("messages")}
              data-testid="lane-messages"
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors",
                lane === "messages" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <MessageSquare className="w-4 h-4 shrink-0" />
              <span>Messages</span>
            </button>
          </div>
        </div>

        {lane === "warm" && (
          <div className="flex items-center gap-1 border-b" data-testid="email-hub-folder-tabs">
            {FOLDERS.map(({ key, label, icon: Icon }) => {
              const active = folder === key;
              const locked = READ_DEPENDENT_FOLDERS.includes(key) && !readAvailable && !gmailStatusLoading;
              return (
                <button
                  key={key}
                  onClick={() => setFolder(key)}
                  data-testid={`rail-${key}`}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
                    active
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{label}</span>
                  {key === "outbox" && outboxCount > 0 && (
                    <span
                      className={cn(
                        "ml-0.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold leading-none",
                        outboxNeedsReconnect
                          ? "bg-destructive text-destructive-foreground"
                          : "bg-amber-500 text-white",
                      )}
                      title={
                        outboxNeedsReconnect
                          ? `${outboxCount} email${outboxCount === 1 ? "" : "s"} stuck — reconnect Gmail`
                          : `${outboxCount} email${outboxCount === 1 ? "" : "s"} waiting to send`
                      }
                      data-testid="badge-outbox-count"
                    >
                      {outboxCount > 99 ? "99+" : outboxCount}
                    </span>
                  )}
                  {locked && <Lock className="w-3 h-3 shrink-0 text-muted-foreground" data-testid={`lock-${key}`} />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
        {lane === "messages" ? (
          <MessengerPanel />
        ) : lane === "cold" ? (
          <ColdFolderView />
        ) : (
          <>
            {READ_DEPENDENT_FOLDERS.includes(folder) && gmailStatusLoading ? (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : READ_DEPENDENT_FOLDERS.includes(folder) && !readAvailable ? (
              <InboxComingSoon state={gmailStatus?.readAccessState} />
            ) : (
              <>
                {folder === "inbox" && <InboxPanel />}
                {folder === "drafts" && <GmailFolderView folder="drafts" />}
                {folder === "sent" && <GmailFolderView folder="sent" />}
              </>
            )}
            {folder === "outbox" && (
              <EmailDraftsTab
                initialDraftId={outboxInitialDraftId}
                onClearInitialDraft={() => {
                  setOutboxInitialDraftId(null);
                  if (typeof window !== "undefined") {
                    const url = new URL(window.location.href);
                    if (url.searchParams.has("draftId")) {
                      url.searchParams.delete("draftId");
                      window.history.replaceState({}, "", url.toString());
                    }
                  }
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
